/**
 * ALT cart drawer + /cart page hardening.
 *
 * Runs once per cart root present on the page: `#cart-drawer` (the drawer,
 * loaded from sections/cart-drawer.liquid only when template.name != 'cart')
 * and/or `[data-cart-page]` (the /cart page, sections/cart.liquid). A page
 * never has both, so this only ever binds twice in the theme editor's
 * preview of the cart template. Jobs 1 and 2 (checkout-total sync, the
 * status region) are drawer-only per Carlin's spec; jobs 3-4 (stepper state,
 * its slider arrows) run on every root. The locked Wear It With / upsell
 * widget itself (no longer collapsible at all, not just backstopped against
 * toggling; see snippets/cart-block-upsell.liquid, 2026-09-28 review fix)
 * needs no JS lock any more.
 *
 * 1. Checkout total sync (drawer only). theme.dev.js `build()` re-renders
 *    only `[data-cart-price-holder]` (and the line items) on every cart
 *    change; the checkout button itself is never touched, so a total baked
 *    into it at render time would go stale after the first add/remove/qty
 *    change. Re-reads the already-Liquid-formatted `[data-cart-total]` text
 *    inside the price holder and copies it into the button's total span,
 *    formatted as "$X - Checkout" (price first, then a plain hyphen,
 *    Carlin 2026-09-28), once on load and again on every price-holder
 *    mutation. On any error, or once the price-holder text is gone/zero,
 *    the total is cleared so the button falls back to plain "Checkout"
 *    rather than showing a stale number.
 * 2. Screen-reader total announcement (drawer only). A visually-hidden
 *    `[role=status][aria-live=polite]` span, mounted at init as a *direct
 *    child of the root* (`#cart-drawer` itself, never inside `.cart__form`
 *    or a `.cart-block`), announces "Total <amount>" whenever the synced
 *    total text changes (never on the first sync, which would just announce
 *    the page load), including the transition to a zero/empty cart. It has
 *    to live outside `.cart__form`/`.cart-block` specifically: both go
 *    `visibility: hidden` and/or `height: 0` under `.is-empty` (theme.css
 *    ~15986 `.is-empty .cart__form`, ~6199
 *    `.is-empty .cart-block:not(.cart-block--top)`), which silenced this
 *    exact announcement whenever the cart emptied out (2026-09-28 review).
 * 3. Quantity stepper state (every root). assets/quantity-counter.js updates
 *    the input's value on click/type but never touches the `<quantity-counter
 *    data-qty>` attribute (kept in sync here mostly for other consumers; the
 *    2026-09-28 CSB-style row no longer keys any icon swap off it, the minus
 *    icon is now constant at every quantity), so the decrease button's
 *    aria-label (Remove at quantity 1, else decrease;
 *    snippets/cart-line-items.liquid) went stale until the next full AJAX
 *    re-render. Delegated input/change listeners on
 *    `.cart__quantity-field` keep both current immediately. Broadcast's own
 *    `resetLineItem()` (theme.dev.js ~1788, run when the server rejects a
 *    quantity change) sets the field back to its `value` attribute and clears
 *    `.is-loading` without dispatching an event, so a class observer on each
 *    `.cart__item` re-runs the same sync whenever `.is-loading` is removed,
 *    catching the rejected-update case too. Also re-synced on a bfcache
 *    restore (below).
 * 4. Wear It With / upsell slider prev/next arrows (every root, 2026-09-28).
 *    The widget's locked markup (snippets/cart-block-upsell.liquid) renders a
 *    `[data-wiw-nav]` button pair (hidden by default) next to the heading
 *    text. This job measures each `.cart__widget__products--slider` for real
 *    horizontal overflow and only then reveals its arrows, clicking one
 *    scrolls by one card's width plus the flex gap (measured live off the
 *    first *visible* card, not hardcoded and not just `firstElementChild`,
 *    so a hidden/zero-width GWP-skip card or a future card-size change in
 *    theme.custom.css doesn't desync the step). A ResizeObserver and a
 *    childList MutationObserver on the slider re-run that overflow check
 *    after a viewport resize or a content change (e.g. a genuine
 *    'upsell-products' block's AJAX item swap; a 'product-list' / Wear It
 *    With block's own items are never AJAX-swapped at all, see cart-price
 *    holder note below); a scroll listener (rAF-throttled) keeps the
 *    disabled/enabled state current while scrolling. On /cart this never
 *    reveals anything: `.cart__widget__products--slider`'s horizontal-scroll
 *    CSS in theme.custom.css is scoped to `#cart-drawer` only, so the /cart
 *    page's cards render in the base theme's grid and never overflow.
 *    2026-09-28 review fix (robustness): the vendor's `disableCartButtons()`/
 *    `enableCartButtons()` (theme.dev.js ~1800-1841) toggle the `disabled`
 *    attribute on *every* button in the cart on each mutation, arrows
 *    included, which can leave stale enabled/disabled state once the
 *    blanket toggle runs. `disabled` is now only ever assigned when the
 *    value actually changes (so a MutationObserver on the nav watching
 *    `attributeFilter: ['disabled']` can re-run `updateNavState()` without
 *    looping on its own writes), and focus moves to the other arrow if the
 *    currently focused one becomes disabled.
 *
 * Automatic cart-refresh recovery (a per-row "stuck loading" watchdog that
 * dispatched the theme's own `theme:cart:refresh`) was removed entirely
 * after a 2026-09-28 review found it worse than doing nothing: any
 * timer-based recovery can refresh the cart while a slower update is still
 * genuinely in flight, re-enabling controls out from under it. Cart-update
 * failures now behave exactly like the vendor theme with no cart JS on the
 * page at all -- a stuck row stays stuck until the page is reloaded.
 *
 * The price holder gets replaced wholesale on each cart re-render (job 1's
 * `priceHolder` reference goes stale), so a second, drawer-level observer
 * re-queries `[data-cart-price-holder]` and rebinds job 1's observer to the
 * new node whenever it changes. The Wear It With / upsell widget itself is a
 * separate subtree from `[data-items-holder]` and is never touched by
 * `build()`'s innerHTML swaps, so its nav buttons (job 4) never need to
 * survive a re-render in the first place; only a genuine 'upsell-products'
 * block's *item cards* get swapped in place, which job 4's observers exist
 * to catch.
 *
 * Fails open throughout: any error leaves the button reading plain
 * "Checkout" (rather than "$X - Checkout") and the rest of the cart
 * working as the base theme would.
 * Idempotent: a root is only ever bound once (guarded via a property on the
 * element itself), so this is safe to run twice, e.g. if a template ever
 * loads the script from two places at once.
 * 2026-09-28.
 */
(function () {
  'use strict';

  try {
    var roots = [];
    var drawerRoot = document.getElementById('cart-drawer');
    if (drawerRoot) roots.push(drawerRoot);
    var pageRoot = document.querySelector('[data-cart-page]');
    if (pageRoot && pageRoot !== drawerRoot) roots.push(pageRoot);

    if (!roots.length) return;

    roots.forEach(initCartRoot);
  } catch (error) {
    /* fail open: nothing wired up, base theme cart still works */
  }

  function initCartRoot(root) {
    if (!root || root.__altCartFooterBound) return;
    root.__altCartFooterBound = true;

    try {
      var isDrawer = root.id === 'cart-drawer';
      var itemsHolder = root.querySelector('[data-items-holder]');

      /* ---------- jobs 1 + 2: checkout total sync + status region (drawer only) ---------- */

      if (isDrawer) {
        bindCheckoutTotalSync(root);
      }

      /* ---------- job 3: keep the stepper's trash/minus + aria-label current ---------- */

      function productTitle(itemEl) {
        var titleEl = itemEl.querySelector('.cart__item__title');
        return titleEl ? (titleEl.textContent || '').replace(/^\s+|\s+$/g, '') : '';
      }

      function removeLabel(itemEl) {
        var link = itemEl.querySelector('.cart__quantity-counter > .cart__item__remove');
        var text = link ? (link.textContent || '').replace(/^\s+|\s+$/g, '') : '';
        return text || 'Remove';
      }

      function syncStepperState(itemEl, field) {
        if (!itemEl || !field) return;

        var counter = itemEl.querySelector('quantity-counter');
        var minusBtn = itemEl.querySelector('.cart__quantity-minus');
        var qty = field.value;

        if (counter) {
          counter.setAttribute('data-qty', qty);
        }

        if (minusBtn) {
          if (String(qty) === '1') {
            minusBtn.setAttribute('aria-label', removeLabel(itemEl) + ' - ' + productTitle(itemEl));
          } else {
            /* the decrease-quantity variant never changes with qty, so the
               button's own (Liquid-rendered) title already holds it verbatim */
            var decreaseAria = minusBtn.getAttribute('title');
            if (decreaseAria) {
              minusBtn.setAttribute('aria-label', decreaseAria);
            }
          }
        }
      }

      function onQuantityFieldEvent(event) {
        try {
          var field = event.target;
          if (!field || !field.classList || !field.classList.contains('cart__quantity-field')) return;

          var itemEl = field.closest ? field.closest('.cart__item') : null;
          if (!itemEl) return;

          syncStepperState(itemEl, field);
        } catch (fieldError) {
          /* fail open: leave the stepper's existing state */
        }
      }

      root.addEventListener('input', onQuantityFieldEvent);
      root.addEventListener('change', onQuantityFieldEvent);

      /* ---------- job 3 cont'd: resync after a rejected update (items observer + bfcache) ---------- */

      function handleItemsMutations(mutations) {
        mutations.forEach(function (mutation) {
          try {
            if (mutation.type !== 'attributes' || mutation.attributeName !== 'class') return;

            var target = mutation.target;

            /* Only `.cart__item` itself, never a descendant and never any
               other element: cart thumbnails carry `lazy-image is-loading`
               / `img.is-loading`, and Wear It With add buttons are
               legitimately `disabled` for unavailable variants. Neither
               has anything to do with this row's own loading state. */
            if (!target.classList || !target.classList.contains('cart__item')) return;

            var wasLoading = (mutation.oldValue || '').indexOf('is-loading') !== -1;
            var isLoading = target.classList.contains('is-loading');

            /* Only the loading->resolved transition matters here:
               resetLineItem() (theme.dev.js ~1788, run when the server
               rejects a quantity change) clears is-loading without
               dispatching an event, and doesn't touch data-qty or the minus
               button's aria-label, so re-sync from the field's current
               (possibly just-reverted) value. */
            if (isLoading || !wasLoading) return;

            var field = target.querySelector('.cart__quantity-field');
            if (field) {
              syncStepperState(target, field);
            }
          } catch (mutationError) {
            /* fail open: skip this mutation record */
          }
        });
      }

      if (itemsHolder && typeof MutationObserver !== 'undefined') {
        var itemsObserver = new MutationObserver(handleItemsMutations);
        itemsObserver.observe(itemsHolder, {
          subtree: true,
          attributes: true,
          attributeFilter: ['class'],
          attributeOldValue: true
        });
      }

      /* A bfcache restore freezes the page mid-interaction; re-sync
         data-qty and the minus button's aria-label from the (possibly
         server-diverged) field values rather than trusting whatever state
         the page was frozen in. */
      window.addEventListener('pageshow', function (event) {
        if (!event.persisted) return;

        try {
          var fields = root.querySelectorAll('.cart__quantity-field');
          for (var i = 0; i < fields.length; i++) {
            var field = fields[i];
            var itemEl = field.closest ? field.closest('.cart__item') : null;
            if (itemEl) syncStepperState(itemEl, field);
          }
        } catch (pageshowError) {
          /* fail open: leave the page as the bfcache restored it */
        }
      });

      /* ---------- job 4: Wear It With / upsell slider prev/next arrows ---------- */

      var navWidgets = root.querySelectorAll('[data-wiw-nav]');
      for (var navIndex = 0; navIndex < navWidgets.length; navIndex++) {
        bindSliderNav(navWidgets[navIndex]);
      }

      function bindSliderNav(navWrap) {
        try {
          var widget = navWrap.closest('[data-upsell-widget]');
          if (!widget) return;

          var slider = widget.querySelector('.cart__widget__products--slider');
          var prevBtn = navWrap.querySelector('[data-wiw-prev]');
          var nextBtn = navWrap.querySelector('[data-wiw-next]');
          if (!slider || !prevBtn || !nextBtn) return;

          /* First *visible* child, not firstElementChild: the GWP logic
             hides a skipped card's holder with [hidden] (theme.custom.css
             collapses its `quick-add-product` wrapper via :has(), but the
             wrapper element itself, and thus firstElementChild, can still
             be a display:none / zero-width node), which would otherwise
             desync the scroll step from the real card width. */
          function firstVisibleCard() {
            var children = slider.children;
            for (var i = 0; i < children.length; i++) {
              var child = children[i];
              var rect = child.getBoundingClientRect();
              if (rect.width > 0 && window.getComputedStyle(child).display !== 'none') {
                return child;
              }
            }
            return null;
          }

          function stepSize() {
            var firstCard = firstVisibleCard();
            var cardWidth = firstCard ? firstCard.getBoundingClientRect().width : slider.clientWidth;
            var style = window.getComputedStyle(slider);
            var gapValue = parseFloat(style.columnGap || style.gap) || 0;
            return cardWidth + gapValue;
          }

          /* 2026-09-28 review fix: only assign .disabled when the value
             actually changes, so the MutationObserver below (which watches
             this same attribute to catch the vendor's own blanket
             disable/enable toggle) doesn't loop on writes it caused itself.
             Also moves focus off an arrow that becomes disabled while
             focused, rather than leaving focus stranded on a dead control. */
          function setDisabled(btn, value) {
            if (btn.disabled === value) return;
            btn.disabled = value;
            if (value && document.activeElement === btn) {
              var other = btn === prevBtn ? nextBtn : prevBtn;
              if (other && !other.disabled) other.focus();
            }
          }

          function updateNavState() {
            try {
              var maxScroll = slider.scrollWidth - slider.clientWidth;
              var overflows = maxScroll > 1;

              if (!overflows) {
                navWrap.setAttribute('hidden', 'hidden');
                return;
              }

              navWrap.removeAttribute('hidden');
              setDisabled(prevBtn, slider.scrollLeft <= 1);
              setDisabled(nextBtn, slider.scrollLeft >= maxScroll - 1);
            } catch (updateError) {
              /* fail open: leave the arrows in whatever state they were */
            }
          }

          /* 2026-09-28 review fix: the vendor's disableCartButtons()/
             enableCartButtons() (theme.dev.js ~1800-1841) toggle `disabled`
             on every button in the cart, arrows included, whenever any cart
             mutation is in flight -- regardless of scroll position -- which
             can leave these arrows enabled past the end of the slider (or
             disabled when they shouldn't be) once that blanket toggle
             clears. Re-run our own state whenever `disabled` changes for a
             reason that wasn't setDisabled() above. */
          if (typeof MutationObserver !== 'undefined') {
            var disabledObserver = new MutationObserver(function () {
              requestAnimationFrame(updateNavState);
            });
            disabledObserver.observe(navWrap, {
              attributes: true,
              attributeFilter: ['disabled'],
              subtree: true
            });
          }

          prevBtn.addEventListener('click', function () {
            slider.scrollBy({left: -stepSize(), behavior: 'smooth'});
          });
          nextBtn.addEventListener('click', function () {
            slider.scrollBy({left: stepSize(), behavior: 'smooth'});
          });

          var scrollTicking = false;
          slider.addEventListener(
            'scroll',
            function () {
              if (scrollTicking) return;
              scrollTicking = true;
              requestAnimationFrame(function () {
                scrollTicking = false;
                updateNavState();
              });
            },
            {passive: true}
          );

          if (typeof ResizeObserver !== 'undefined') {
            var resizeObserver = new ResizeObserver(function () {
              requestAnimationFrame(updateNavState);
            });
            resizeObserver.observe(slider);
          }

          if (typeof MutationObserver !== 'undefined') {
            var contentObserver = new MutationObserver(function () {
              requestAnimationFrame(updateNavState);
            });
            contentObserver.observe(slider, {childList: true, subtree: true});
          }

          window.addEventListener('resize', function () {
            requestAnimationFrame(updateNavState);
          });

          requestAnimationFrame(updateNavState);
        } catch (bindError) {
          /* fail open: no arrows, slider still usable by touch/trackpad scroll */
        }
      }

    } catch (rootError) {
      /* fail open: this root gets none of the hardening, base theme still works */
    }
  }

  function bindCheckoutTotalSync(root) {
    var totalSpan = root.querySelector('[data-alt-checkout-total]');
    var priceHolder = null;
    var priceObserver = null;
    var previousFormatted = null;
    var statusSpan = null;

    function ensureStatusSpan() {
      if (statusSpan && root.contains(statusSpan)) return statusSpan;

      statusSpan = root.querySelector(':scope > [data-alt-cart-total-status]');
      if (statusSpan) return statusSpan;

      statusSpan = document.createElement('span');
      statusSpan.setAttribute('role', 'status');
      statusSpan.setAttribute('aria-live', 'polite');
      statusSpan.className = 'visually-hidden';
      statusSpan.setAttribute('data-alt-cart-total-status', '');

      /* Direct child of the drawer root itself, deliberately never inside
         .cart__form or a .cart-block: see job 2's header note for why. */
      root.appendChild(statusSpan);

      return statusSpan;
    }

    /* Mount the status region now, not lazily on the first announcement. */
    try {
      ensureStatusSpan();
    } catch (mountError) {
      /* fail open: it will still be created lazily by announce() below */
    }

    function clearTotal() {
      if (!totalSpan) return;
      totalSpan.textContent = '';
      totalSpan.setAttribute('hidden', 'hidden');
    }

    function announce(text) {
      try {
        var span = ensureStatusSpan();
        if (span) {
          span.textContent = text;
        }
      } catch (announceError) {
        /* fail open: no announcement, drawer still works */
      }
    }

    function sync() {
      try {
        if (!priceHolder || !totalSpan) return;

        var cartTotalEl = priceHolder.querySelector('[data-cart-total]');
        var formatted = '';
        var rawText = '';

        if (cartTotalEl) {
          var rawTotal = cartTotalEl.getAttribute('data-cart-total');
          rawText = (cartTotalEl.textContent || '').replace(/^\s+|\s+$/g, '');

          if (rawTotal && rawTotal !== '0') {
            formatted = rawText;
          }
        }

        if (!formatted) {
          clearTotal();
        } else {
          /* price first, then a plain hyphen (Carlin 2026-09-28): "USD $406.30 - Checkout".
             Non-breaking spaces (escaped, not literal source bytes -- see
             2026-09-28 review nit) keep "USD $406.30 - Checkout" from
             wrapping mid-separator in a narrow button. */
          totalSpan.textContent = formatted + ' - ';
          totalSpan.removeAttribute('hidden');
        }

        if (previousFormatted !== null && formatted !== previousFormatted) {
          if (formatted) {
            announce('Total ' + formatted);
          } else {
            /* zero-total transition: still announce something concrete
               rather than going silent when the cart empties out */
            announce(rawText ? 'Total ' + rawText : 'Cart is empty');
          }
        }
        previousFormatted = formatted;
      } catch (innerError) {
        /* fail open: clear the total rather than leave a stale one showing */
        clearTotal();
      }
    }

    /* ---------- price-holder rebinding (survives a drawer re-render) ---------- */

    function bindPriceHolder(holder) {
      if (priceObserver) {
        priceObserver.disconnect();
        priceObserver = null;
      }

      priceHolder = holder;
      if (!priceHolder) return;

      sync();

      if (typeof MutationObserver !== 'undefined') {
        priceObserver = new MutationObserver(sync);
        priceObserver.observe(priceHolder, {
          childList: true,
          subtree: true,
          characterData: true
        });
      }
    }

    function checkPriceHolder() {
      try {
        var current = root.querySelector('[data-cart-price-holder]');
        if (current && current !== priceHolder) {
          bindPriceHolder(current);
        }
      } catch (rebindError) {
        /* fail open: keep whatever binding already exists */
      }
    }

    bindPriceHolder(root.querySelector('[data-cart-price-holder]'));

    if (typeof MutationObserver !== 'undefined') {
      var drawerObserver = new MutationObserver(checkPriceHolder);
      drawerObserver.observe(root, {childList: true, subtree: true});
    }
  }
})();
